import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";

import type { FlotiqStore, ShopSchedule } from "@jti/fe-common";

import type { StoreLocatorSelectedFilter, StoreLocatorWithinCategoryFilterOperator } from "../types";

import { OPEN_NOW_CATEGORY } from "./openNowConst";

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

type SelectedFilter = Pick<StoreLocatorSelectedFilter, "key" | "category">;

type SelectedFiltersByCategory = Map<string, SelectedFilter[]>;

const getSelectedFilters = (selectedFilters: Record<string, StoreLocatorSelectedFilter>): SelectedFilter[] =>
  Object.entries(selectedFilters)
    .filter(([, { selected }]) => selected)
    .map(([, { category, key }]) => ({ category, key }));

const getCurrentDayWithTime = (time: string) => {
  const today = dayjs();
  const [hours, minutes] = time.split(":");

  const dateWithTime = today.hour(parseInt(hours)).minute(parseInt(minutes)).second(0);

  return dateWithTime;
};

const isStoreOpen = (store: FlotiqStore) => {
  if (!store.opening_hours) return false;

  const now = dayjs();
  const currentStoreDay: ShopSchedule = store.opening_hours[0]?.days[now.isoWeekday() - 1];

  if (!currentStoreDay) return false;

  const { open_time, close_time, break_end_time, break_start_time } = currentStoreDay;
  const openTime = getCurrentDayWithTime(open_time);
  const closeTime = getCurrentDayWithTime(close_time);

  if (!break_end_time) {
    return now.isBetween(openTime, closeTime);
  }
  const breakStartTime = getCurrentDayWithTime(break_start_time);
  const breakEndTime = getCurrentDayWithTime(break_end_time);

  return now.isBetween(openTime, breakStartTime) || now.isBetween(breakEndTime, closeTime);
};

const hasStoreProductAttributeValue = (store: FlotiqStore, key: string, category: string) => {
  if (!store.product_attributes) return false;

  const storeProductAttributes = store?.product_attributes;
  const storeProductAttributesVending = store?.product_attributes_vending_machine;

  const hasProductAttributeValueInCategory = (innerCategory: string) => {
    const storeCategory = storeProductAttributes?.find((attribute) => attribute.key === innerCategory);
    const storeCategoryVending = storeProductAttributesVending?.find((attribute) => attribute.key === innerCategory);

    return (
      storeCategory?.values.some((v) => v.value === key) ??
      storeCategoryVending?.values.some((v) => v.value === key) ??
      false
    );
  };

  if (category === "ProductFlavours") {
    return [
      "ProductFlavours",
      "ProductFlavoursLYO",
      "ProductFlavoursVendingMachine",
      "ProductFlavoursLYOVendingMachine",
    ].some(hasProductAttributeValueInCategory);
  }

  if (category === "ProductColors") {
    return hasProductAttributeValueInCategory("ProductColors");
  }

  return false;
};

const isStoreMatchingSelectedFilter = (store: FlotiqStore, { key, category }: SelectedFilter) => {
  switch (category) {
    case OPEN_NOW_CATEGORY:
      return isStoreOpen(store);
    case "ProductColors":
    case "ProductFlavours":
      return hasStoreProductAttributeValue(store, key, category);
    case "storetype":
      return store.storetype === key;
    case "product_types":
    case "product_types_vending_machine":
      return (
        store.product_types?.some((filter) => filter.key === key && filter.is_available === "true") ||
        store.product_types_vending_machine?.some((filter) => filter.key === key && filter.is_available === "true")
      );
    case "services":
      return store.services?.some((filter) => filter.key === key && filter.is_available === "true");
    default:
      return false;
  }
};

const groupSelectedFiltersByCategory = (selectedFilters: SelectedFilter[]) =>
  selectedFilters.reduce<SelectedFiltersByCategory>((selectedFiltersByCategory, selectedFilter) => {
    const categoryFilters = selectedFiltersByCategory.get(selectedFilter.category) ?? [];
    categoryFilters.push(selectedFilter);
    selectedFiltersByCategory.set(selectedFilter.category, categoryFilters);

    return selectedFiltersByCategory;
  }, new Map());

const isStoreMatchingFilterCategory = (
  store: FlotiqStore,
  selectedFilters: SelectedFilter[],
  withinCategoryFilterOperator: StoreLocatorWithinCategoryFilterOperator
) => {
  if (withinCategoryFilterOperator === "and") {
    return selectedFilters.every((selectedFilter) => isStoreMatchingSelectedFilter(store, selectedFilter));
  }

  return selectedFilters.some((selectedFilter) => isStoreMatchingSelectedFilter(store, selectedFilter));
};

const isStoreMatchingSelectedFilters = (
  store: FlotiqStore,
  selectedFilters: Record<string, StoreLocatorSelectedFilter>,
  withinCategoryFilterOperator: StoreLocatorWithinCategoryFilterOperator
) => {
  const selectedFiltersKeys = getSelectedFilters(selectedFilters);

  if (selectedFiltersKeys.length === 0) {
    return true;
  }

  const selectedFiltersByCategory = groupSelectedFiltersByCategory(selectedFiltersKeys);

  return Array.from(selectedFiltersByCategory.values()).every((categoryFilters) =>
    isStoreMatchingFilterCategory(store, categoryFilters, withinCategoryFilterOperator)
  );
};

export const getStoresFilteredBySelectedFilters = (
  stores: FlotiqStore[],
  selectedFilters: Record<string, StoreLocatorSelectedFilter>,
  withinCategoryFilterOperator: StoreLocatorWithinCategoryFilterOperator = "or"
) => stores.filter((store) => isStoreMatchingSelectedFilters(store, selectedFilters, withinCategoryFilterOperator));
